import moment from 'moment'
import { AppStore } from '../stores/AppStore.js'
import { api, getSignature } from './apiFunctions'
import { convertFieldResponseIntoMuiTextFieldProps } from '@mui/x-date-pickers/internals'

/**
 * Validates if required fields in People Builder settings are empty
 * @param {Object} data - The data object to validate
 * @param {Function} setStartDateEmpty - State setter for start date error
 * @param {Function} setEndDateEmpty - State setter for end date error
 * @param {Function} setMidYearEmpty - State setter for mid year error
 * @param {Function} setEndYearEmpty - State setter for end year error
 * @param {Function} setPdfEmpty - State setter for PDF error
 * @returns {boolean} - Returns true if there are empty fields
 */
export const hasEmptyFields = (
    data,
    setStartDateEmpty,
    setEndDateEmpty,
    setMidYearEmpty,
    setEndYearEmpty,
    setPdfEmpty
) => {
    let hasEmptyFields = false
    const fieldsFromPbSettingsToCheck = ['start_date', 'end_date', 'mid_year_checkin', 'end_year_checkin', 'file_url']

    Object.keys(data).forEach(item => {
        if (fieldsFromPbSettingsToCheck.includes(item)) {
            if (item === 'start_date' && !data[item]) {
                setStartDateEmpty({ error: true, errorText: 'Start Date cannot be empty' })
                hasEmptyFields = true
            }

            if (item === 'end_date' && !data[item]) {
                setEndDateEmpty({ error: true, errorText: 'End Date cannot be empty' })
                hasEmptyFields = true
            }

            if (item === 'mid_year_checkin' && !data[item]) {
                setMidYearEmpty({ error: true, errorText: 'Mid Year Check In cannot be empty' })
                hasEmptyFields = true
            }

            if (item === 'end_year_checkin' && !data[item]) {
                setEndYearEmpty({ error: true, errorText: 'End Year Check In cannot be empty' })
                hasEmptyFields = true
            }

            if (item === 'file_url' && !data[item]) {
                setPdfEmpty({ error: true, errorText: 'Goals Settings File cannot be empty' })
                hasEmptyFields = true
            }
        }
    })

    return hasEmptyFields
}

/**
 * Determines the status of a People Builder based on start and end dates
 * @param {Date} startDate - The start date
 * @param {Date} endDate - The end date
 * @returns {string} - Returns 'active', 'next', or 'previous'
 */
export const getPeopleBuilderStatus = (startDate, endDate) => {
    const now = Date.now(); // number
    const start = new Date(startDate).getTime(); // number
    const end = new Date(endDate).getTime();     // number
  
    // optional safety if dates are invalid
    if (Number.isNaN(start) || Number.isNaN(end)) {
      console.warn('Invalid start/end date:', { startDate, endDate });
      return 'previous';
    }
  
    if (now >= start && now <= end) {
      return 'active';
    } else if (now < start) {
      return 'next';
    } else {
      return 'previous';
    }
  };
  

/**
 * Creates check-in reminders for a People Builder
 * @param {number} id - The People Builder settings ID
 * @param {Array} reminders - Array of reminder objects
 * @param {Function} openAlert - Function to show alert messages
 */
export const createCheckInReminders = async (id, reminders, openAlert) => {
    try {
        await api.post('/emails-tracking', reminders)
    } catch (error) {
        console.log(error)
        openAlert('Error creating monthly reminders', 'error')
    }
}

/**
 * Updates check-in reminders for a People Builder
 * @param {number} id - The People Builder settings ID
 * @param {Array} reminders - Array of reminder objects
 * @param {Function} openAlert - Function to show alert messages
 */
export const updateCheckInReminders = async (id, reminders, openAlert) => {
    try {
        await api.put(`/emails-tracking/${id}`, reminders)
    } catch (error) {
        console.log(error)
        openAlert('Error updating monthly reminders', 'error')
    }
}

/**
 * Creates an array of reminder objects for check-ins
 * @param {moment.Moment} startDate - The start date
 * @param {moment.Moment} endDate - The end date
 * @param {Object} pbData - People Builder data containing mid_year_checkin and end_year_checkin
 * @param {number} id - The People Builder settings ID
 * @returns {Array} - Array of reminder objects
 */
export const createReminders = (startDate, endDate, pbData, id) => {
    const reminders = []
    let currentDate = moment(startDate).add(1, 'month')

    reminders.push({
        type: 'start ppb reminder',
        recipients_type: 'players',
        status: 'pending',
        source: 'people-builder',
        plan_date: moment(startDate),
        people_builder_settings_id: id
    })

    while (currentDate.isBefore(endDate)) {
        let planDate = currentDate.toDate()
        const month = currentDate.month()
        const midYearMonth = moment(pbData.mid_year_checkin).month()
        const endYearMonth = moment(pbData.end_year_checkin).month()
        const checkInType = month === midYearMonth || month === endYearMonth ? 'required check-in reminder' : 'monthly check-in reminder'

        // Adjust to the next weekday if the date falls on a weekend
        if (currentDate.day() === 0) { // Sunday
            planDate = currentDate.add(1, 'day').toDate()
        } else if (currentDate.day() === 6) { // Saturday
            planDate = currentDate.add(2, 'days').toDate()
        }

        reminders.push({
            type: checkInType,
            recipients_type: 'players',
            status: 'pending',
            source: 'people-builder',
            plan_date: planDate,
            people_builder_settings_id: id
        })
        reminders.push({
            type: checkInType,
            recipients_type: 'coaches',
            status: 'pending',
            source: 'people-builder',
            plan_date: planDate,
            people_builder_settings_id: id
        })

        currentDate = currentDate.add(1, 'month')
    }

    return reminders
}

/**
 * Sends a test email
 * @param {string} userEmail - The user's email address
 * @param {string} emailText - The email content
 * @param {Function} openAlert - Function to show alert messages
 * @param {Function} setOpenBackdrop - Function to control backdrop state
 */
export const sendEmailTest = async (userEmail, emailText, openAlert, setOpenBackdrop) => {
    setOpenBackdrop(true)
    const data = {
        to: userEmail,
        subject: 'Test Email',
        content: emailText + getSignature(),
    }

    try {
        await api.post('/send-emails', data)
        openAlert('Email sent successfully', 'success')
        setOpenBackdrop(false)
    } catch (error) {
        console.log(error)
        setOpenBackdrop(false)
        openAlert('Error sending email', 'error')
    }
}

/**
 * Sends a test Slack message
 * @param {Object} message - The Slack message object
 * @param {Function} openAlert - Function to show alert messages
 */
export const sendSlackMessageTest = async (message, openAlert) => {
    try {
        await api.post('/slack-messages', message)
        openAlert('Slack message sent successfully', 'success')
    } catch (error) {
        console.log(error)
        openAlert('Error sending Slack message', 'error')
    }
}

/**
 * Extracts content from email editor and returns plain text and HTML
 * @param {string} editorContent - The HTML content from the editor
 * @returns {Object} - Object with content and text properties
 */
export const getContentEmail = (editorContent) => {
    const plainText = editorContent.replace(/<[^>]+>/g, '')
    const value = plainText.replaceAll('&nbsp;', ' ')
    return { content: editorContent, text: value }
}

/**
 * Checks if a People Builder already exists for a given year
 * @param {Array} peopleBuilderSettings - Array of existing People Builder settings
 * @param {Date} startDate - The start date of the new People Builder
 * @returns {boolean} - Returns true if a People Builder exists for the year
 */
export const checkExistingPeopleBuilder = (peopleBuilderSettings, startDate) => {
    const existingPb = peopleBuilderSettings.filter(pb => {
        const currYear = new Date(startDate).getUTCFullYear()
        const year = new Date(pb.start_date).getUTCFullYear()
        return year === currYear
    })

    return existingPb.length > 0
}

/**
 * Creates a new People Builder Settings
 * @param {Object} data - The data to create
 * @param {Object} pbData - Additional People Builder data
 * @param {Function} openAlert - Function to show alert messages
 * @returns {Object} - Returns the created data with ID
 */
export const createPeopleBuilderSettingsAPI = async (data, pbData, openAlert) => {
    try {
        const response = await api.post('/people-builder-settings', data)

        if (response.status === 200) {
            const id = response.data.id
            const pbResponse = await api.post(`/people-builders/${id}`, data)

            return {
                success: true,
                id,
                pbResponse: pbResponse.data,
                data: response.data
            }
        }

        return { success: false }
    } catch (error) {
        console.log(error)
        openAlert('Error on create People Builder Settings', 'error')
        return { success: false, error }
    }
}

/**
 * Updates an existing People Builder Settings
 * @param {number} id - The People Builder Settings ID
 * @param {Object} data - The data to update
 * @param {Function} openAlert - Function to show alert messages
 * @returns {Object} - Returns success status and response data
 */
export const updatePeopleBuilderSettingsAPI = async (id, data, openAlert) => {
    try {
        const response = await api.post(`/people-builder-settings/${id}`, data)
        return {
            success: true,
            data: response.data
        }
    } catch (error) {
        console.log(error)
        openAlert('Error on update People Builder Settings', 'error')
        return { success: false, error }
    }
}

export const syncPeopleBuilderStatuses = async (pbSettings, openAlert) => {
    console.log('firing')
    if (!Array.isArray(pbSettings) || !pbSettings.length) return;
  
    for (const pb of pbSettings) {
      const { id, start_date, end_date, status } = pb;
  
      const computedStatus = getPeopleBuilderStatus(start_date, end_date);
  
      if (computedStatus !== status) {
        await updatePeopleBuilderSettingsAPI(
          id,
          { status: computedStatus },
          openAlert
        );
      }
    }
  };

/**
 * Deletes a People Builder Settings and its reminders
 * @param {number} id - The People Builder Settings ID
 * @param {Function} openAlert - Function to show alert messages
 * @returns {Object} - Returns success status and response data
 */
export const deletePeopleBuilderSettingsAPI = async (id, openAlert) => {
    try {
        const response = await api.delete(`/people-builder-settings/${id}`)

        // Delete reminders
        try {
            await api.delete(`/emails-tracking/${id}`)
        } catch (error) {
            console.log('Error deleting reminders:', error)
        }

        return {
            success: true,
            data: response.data
        }
    } catch (error) {
        console.log(error)
        openAlert('Error on delete People Builder Settings', 'error')
        return { success: false, error }
    }
}

/**
 * Creates People Builder Settings with all validations and updates
 * @param {Object} params - Parameters object
 * @param {moment.Moment} params.startDate - Start date
 * @param {moment.Moment} params.endDate - End date
 * @param {Object} params.pbData - People Builder data
 * @param {Object} params.slackObject - Slack message object
 * @param {string} params.url - Goals settings file URL
 * @param {Array} params.peopleBuilderSettings - Current people builder settings array
 * @param {Function} params.setOpenBackdrop - Function to control backdrop visibility
 * @param {Function} params.setPeopleBuilderSettings - Function to update people builder settings state
 * @param {Function} params.setOpenPeopleBuilderForm - Function to control form visibility
 * @param {Function} params.openAlert - Function to show alert messages
 * @param {Object} params.validationSetters - Object with validation setter functions
 */
export const createPeopleBuilderSettings = async ({
    startDate,
    endDate,
    pbData,
    slackObject,
    url,
    peopleBuilderSettings,
    setOpenBackdrop,
    setPeopleBuilderSettings,
    setOpenPeopleBuilderForm,
    openAlert,
    validationSetters
}) => {
    const status = getPeopleBuilderStatus(startDate, endDate)

    const data = {
        start_date: startDate,
        end_date: endDate,
        status: status,
        mid_year_checkin: pbData.mid_year_checkin,
        end_year_checkin: pbData.end_year_checkin,
        email_message: pbData.email_message,
        slack_message: JSON.stringify(slackObject),
        file_url: url
    }

    const {
        setStartDateEmpty,
        setEndDateEmpty,
        setMidYearEmpty,
        setEndYearEmpty,
        setPdfEmpty
    } = validationSetters

    if (hasEmptyFields(
        data,
        setStartDateEmpty,
        setEndDateEmpty,
        setMidYearEmpty,
        setEndYearEmpty,
        setPdfEmpty
    )) {
        return
    }

    setOpenBackdrop(true)

    if (checkExistingPeopleBuilder(peopleBuilderSettings, data.start_date)) {
        openAlert(`This People Builder already exists, Year: ${new Date(data.start_date).getUTCFullYear()} `, 'error')
        setOpenBackdrop(false)
        return
    }

    const result = await createPeopleBuilderSettingsAPI(data, pbData, openAlert)

    if (result.success) {
        const { id, pbResponse } = result
        const reminders = createReminders(startDate, endDate, pbData, id)
        await createCheckInReminders(id, reminders, openAlert)

        const newPbSetting = {
            ...data,
            id,
            status,
            start_date: startDate.toISOString(),
            end_date: endDate.toISOString(),
            mid_year_checkin: pbData.mid_year_checkin,
            end_year_checkin: pbData.end_year_checkin
        }

        setPeopleBuilderSettings(prev => [...prev, newPbSetting])
        AppStore.update(s => {
            s.pbSettings = s.pbSettings ? [...s.pbSettings, newPbSetting] : [newPbSetting]
        })

        if (pbResponse) {
            if (pbResponse.peopleBuilders) {
                AppStore.update(s => {
                    s.peopleBuilders = pbResponse.peopleBuilders
                })
            }

            if (pbResponse.goals) {
                AppStore.update(s => {
                    s.goals = pbResponse.goals
                })
            }
        }

        openAlert('People Builder Settings created successfully', 'success')
    }

    setOpenPeopleBuilderForm(false)
    setOpenBackdrop(false)
}

/**
 * Loads People Builder Settings for editing
 * @param {Object} params - Parameters object
 * @param {Array} params.pbSettings - All people builder settings
 * @param {number} params.peopleBuilderId - ID of the people builder to load
 * @param {Array} params.months - Array of month names
 * @param {Function} params.setOpenBackdrop - Function to control backdrop visibility
 * @param {Function} params.setStartDate - Function to set start date
 * @param {Function} params.setEndDate - Function to set end date
 * @param {Function} params.setMidYearDate - Function to set mid year date
 * @param {Function} params.setEndYearDate - Function to set end year date
 * @param {Function} params.setUrl - Function to set URL
 * @param {Function} params.setEmailText - Function to set email text
 * @param {Function} params.setPbData - Function to set people builder data
 * @param {Function} params.setSlackObject - Function to set slack object
 */
export const getUserPeopleBuilderSettings = async ({
    pbSettings,
    peopleBuilderId,
    months,
    setOpenBackdrop,
    setStartDate,
    setEndDate,
    setMidYearDate,
    setEndYearDate,
    setUrl,
    setEmailText,
    setPbData,
    setSlackObject
}) => {
    setOpenBackdrop(true)
    try {
        if (!pbSettings || !peopleBuilderId) {
            setOpenBackdrop(false)
            return
        }

        const values = pbSettings.filter(value => value.status !== 'archived' && value.id === peopleBuilderId)
        if (values.length) {
            const moment = await import('moment')
            setStartDate(moment.default(values[0].start_date))
            setEndDate(moment.default(values[0].end_date))
            setMidYearDate(months[moment.default(values[0].mid_year_checkin).month()])
            setEndYearDate(months[moment.default(values[0].end_year_checkin).month()])
            setUrl(values[0].file_url)

            const emailMessage = values[0].email_message || ''
            setEmailText(emailMessage)
            setPbData(prevStates => ({
                ...prevStates,
                status: values[0].status,
                start_date: values[0].start_date,
                end_date: values[0].end_date,
                mid_year_checkin: values[0].mid_year_checkin,
                end_year_checkin: values[0].end_year_checkin,
                file_url: values[0].file_url,
                email_message: emailMessage
            }))

            // Carregar slack_message
            if (values[0].slack_message) {
                let slackMsg = values[0].slack_message

                // Parse se for string JSON
                if (typeof slackMsg === 'string') {
                    try {
                        slackMsg = JSON.parse(slackMsg)
                    } catch (e) {
                        console.error('Error parsing slack_message:', e)
                        slackMsg = { title: '', message: '' }
                    }
                }

                const slackData = {
                    title: slackMsg.title || '',
                    message: slackMsg.message || ''
                }
                setSlackObject(slackData)
                setPbData(prevStates => ({
                    ...prevStates,
                    slack_message: slackData
                }))
            }
        }

        setOpenBackdrop(false)

    } catch (error) {
        console.log(error)
        setOpenBackdrop(false)
    }
}

/**
 * Updates People Builder Settings with all validations and updates
 * @param {Object} params - Parameters object
 * @param {number} params.id - People Builder Settings ID
 * @param {Object} params.data - Data to update
 * @param {moment.Moment} params.startDate - Start date
 * @param {moment.Moment} params.endDate - End date
 * @param {Object} params.pbData - People Builder data
 * @param {Object} params.slackObject - Slack message object
 * @param {string} params.emailText - Email text content
 * @param {Function} params.setOpenBackdrop - Function to control backdrop visibility
 * @param {Function} params.setPeopleBuilderSettings - Function to update people builder settings state
 * @param {Function} params.setOpenPeopleBuilderForm - Function to control form visibility
 * @param {Function} params.openAlert - Function to show alert messages
 */
export const updatePeopleBuilderSettings = async ({
    id,
    data,
    startDate,
    endDate,
    pbData,
    slackObject,
    emailText,
    setOpenBackdrop,
    setPeopleBuilderSettings,
    setOpenPeopleBuilderForm,
    openAlert
}) => {
    setOpenBackdrop(true)

    const dataToUpdate = {
        ...data,
        slack_message: JSON.stringify(slackObject),
        email_message: emailText,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString()
    }

    const result = await updatePeopleBuilderSettingsAPI(id, dataToUpdate, openAlert)

    if (result.success) {
        const reminders = createReminders(startDate, endDate, pbData, id)
        await updateCheckInReminders(id, reminders, openAlert)

        const updatedPbSetting = {
            ...dataToUpdate,
            id
        }

        AppStore.update(s => {
            const index = s.pbSettings?.findIndex(pb => pb.id === id)
            if (index !== -1 && s.pbSettings) {
                s.pbSettings[index] = updatedPbSetting
            }
        })

        setPeopleBuilderSettings(prev =>
            prev.map(pb => pb.id === id ? updatedPbSetting : pb)
        )

        openAlert(result.data.message, 'success')
        setOpenPeopleBuilderForm(false)
    }

    setOpenBackdrop(false)
}

